import React, { Fragment, useEffect, useRef, useState } from 'react';
import Slider from '@material-ui/core/Slider';
import { isMobile } from 'react-device-detect';
import { IconButton } from '@material-ui/core';
import RemoveIcon from '@material-ui/icons/Remove';
import AddIcon from '@material-ui/icons/Add';
import Box from '@material-ui/core/Box';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';

import { get_settings_borders_and_units } from '../Constants/constants';
import { LockSliderContext } from '../Contexts';

function GetMarks(min, mid, max, unit, mod_digits = 1) {
  if (mod_digits === 10 || !Number.isInteger(min) || !Number.isInteger(max)) {
    if (mid === undefined) {
      mid = (min + max) / 2;
    }
    const marks = [
      {
        value: min,
        label: `${min.toFixed(2)}${unit}`
      },
      {
        value: mid,
        label: `${mid.toFixed(2)}${unit}`
      },
      {
        value: max,
        label: `${max.toFixed(2)}${unit}`
      }
    ];
    return marks;
  }

  if (mid === undefined) {
    mid = Math.round((min + max) / 2);
  }
  const marks = [
    {
      value: min,
      label: `${min.toFixed(0)}${unit}`
    },
    {
      value: mid,
      label: `${mid.toFixed(0)}${unit}`
    },
    {
      value: max,
      label: `${max.toFixed(0)}${unit}`
    }
  ];
  return marks;
}

function valuetext(value, unit) {
  return `${value}${unit}`;
}

function estimateMarkLabelWidth(label) {
  return String(label ?? '').length * 8 + 16;
}

const SLIDER_THUMB_CLEARANCE = 12;

function fitSliderMarks(marks, sliderWidth) {
  if (marks.length < 3) {
    return marks;
  }

  const labelsWidth = marks.reduce((sum, mark) => sum + estimateMarkLabelWidth(mark.label), 0);
  const compact = !sliderWidth
    ? labelsWidth > 220
    : sliderWidth < 300 || labelsWidth > sliderWidth - 24;

  if (!compact) {
    return marks;
  }

  return marks.map((mark, index) => (index === 1 ? { ...mark, label: '' } : mark));
}

function sliderValueMarks(marks) {
  return marks.map((mark) => ({ value: mark.value }));
}

function getScaleMinWidth(marks) {
  const visibleMarks = marks.filter((mark) => mark.label !== '');

  if (visibleMarks.length === 0) {
    return 0;
  }

  return visibleMarks.reduce((sum, mark) => sum + estimateMarkLabelWidth(mark.label), 0) + 16;
}

function SliderScale({ marks, classes }) {
  const visibleMarks = marks.filter((mark) => mark.label !== '');

  if (visibleMarks.length === 0) {
    return null;
  }

  return (
    <Box className={classes.scaleRow}>
      {visibleMarks.map((mark, index) => (
        <Box
          key={mark.value}
          style={{
            flex: visibleMarks.length === 1 ? '0 1 auto' : '1 1 0',
            minWidth: 0,
            textAlign:
              index === 0 ? 'left' : index === visibleMarks.length - 1 ? 'right' : 'center',
            whiteSpace: 'nowrap'
          }}
        >
          {mark.label}
        </Box>
      ))}
    </Box>
  );
}

export function MySlider({
  IncClick,
  DecClick,
  InputHandler,
  convert_to_min,
  convert_to_hhmmss,
  wrap_time_over_midnight,
  type,
  id,
  unit_key,
  num_digits = 1,
  mod_digits = 1,
  mod_steps = undefined,
  ...other
}) {
  const classes = useStyles();
  const mobile = isMobile;
  const [lock, setLock] = useState((localStorage.getItem('slider_' + id) || 'true') === 'true');
  const [sliderWidth, setSliderWidth] = useState(0);
  const sliderBoxRef = useRef(null);

  React.useEffect(() => {
    localStorage.setItem('slider_' + id, lock);
  }, [lock]);

  React.useEffect(() => {
    if (!sliderBoxRef.current || typeof ResizeObserver === 'undefined') return;

    let animationFrame;
    const resizeObserver = new ResizeObserver(([entry]) => {
      const nextWidth = Math.round(entry.contentRect.width);
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        setSliderWidth((currentWidth) => (currentWidth === nextWidth ? currentWidth : nextWidth));
      });
    });
    resizeObserver.observe(sliderBoxRef.current);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
    };
  }, []);

  let locked = function (lockSliderSetting) {
    return (
      !(other.disabled || false) &&
      lock &&
      (lockSliderSetting === 'on' || (lockSliderSetting === 'auto' && mobile))
    );
  };

  return (
    <LockSliderContext.Consumer>
      {(value) => (
        <Box>
          <Box
            style={{
              alignItems: 'flex-start',
              display: 'flex',
              gap: 'clamp(12px, 2vw, 20px)',
              justifyContent: 'center',
              flexWrap: 'nowrap',
              width: '100%'
            }}
          >
            {/* minus */}
            <Box
              style={{
                width: 40,
                flex: '0 0 40px',
                display: 'flex',
                alignItems: 'center',
                height: 40,
                justifyContent: 'center'
              }}
            >
              <IconButton
                onClick={DecClick}
                color="inherit"
                aria-label="decrease"
                mb={2}
                disabled={other.disabled || locked(value)}
              >
                <RemoveIcon />
              </IconButton>
            </Box>

            {/* slider */}
            <Box
              ref={sliderBoxRef}
              style={{
                display: 'flex',
                flex: '1 1 0%',
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: 0
              }}
            >
              {type === 'float' ? (
                <FloatSlider
                  {...other}
                  num_digits={num_digits}
                  mod_digits={mod_digits}
                  slider_width={sliderWidth}
                  locked={locked(value)}
                  unit_key={unit_key ?? id}
                />
              ) : type === 'negative' ? (
                <NegativeSlider
                  {...other}
                  slider_width={sliderWidth}
                  locked={locked(value)}
                  unit_key={unit_key ?? id}
                />
              ) : type === 'fan_min_max' ? (
                <MinMaxSlider
                  {...other}
                  slider_width={sliderWidth}
                  locked={locked(value)}
                  unit_key={unit_key ?? id}
                />
              ) : (
                <BaseSlider
                  {...other}
                  convert_to_min={convert_to_min}
                  convert_to_hhmmss={convert_to_hhmmss}
                  wrap_time_over_midnight={wrap_time_over_midnight}
                  mod_steps={mod_steps}
                  slider_width={sliderWidth}
                  locked={locked(value)}
                  unit_key={unit_key ?? id}
                />
              )}
            </Box>

            {/* plus */}
            <Box
              style={{
                width: 40,
                flex: '0 0 40px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 40,
                padding: 0
              }}
            >
              <IconButton
                onClick={IncClick}
                color="inherit"
                aria-label="increase"
                disabled={other.disabled || locked(value)}
              >
                <AddIcon />
              </IconButton>
            </Box>
          </Box>

          {((value === 'auto' && mobile) || value === 'on') && (
            <Box className={classes.sliderLockRow}>
              {lock ? (
                <LockIcon onClick={() => setLock(false)} />
              ) : (
                <LockOpenIcon onClick={() => setLock(true)} />
              )}
            </Box>
          )}
        </Box>
      )}
    </LockSliderContext.Consumer>
  );
}

const StyledSlider = withStyles({
  root: {
    boxSizing: 'border-box',
    height: 40,
    padding: 0,
    '@media (pointer: coarse)': {
      padding: 0
    }
  },
  thumb: {
    height: 18,
    width: 18,
    marginLeft: -9,
    border: '4px solid currentColor',
    marginTop: -9,
    top: '50%'
  },
  active: { color: 'white' },
  track: {
    height: 5,
    borderRadius: 0,
    top: '50%',
    transform: 'translateY(-50%)'
  },
  rail: {
    height: 5,
    borderRadius: 0,
    opacity: 0.5,
    top: '50%',
    transform: 'translateY(-50%)'
  },
  mark: {
    display: 'none'
  },
  markLabel: {
    marginTop: '60px'
  }
})(Slider);

const StyledSliderLocked = withStyles((theme) => ({
  disabled: (props) => ({
    '& .MuiSlider-thumb ': {
      height: 18,
      width: 18,
      marginLeft: -9,
      color: theme.palette[props.color].main,
      border: '4px solid currentColor',
      marginTop: -9,
      top: '50%'
    },
    '& .MuiSlider-track': {
      backgroundColor: theme.palette[props.color].main,
      height: 5,
      borderRadius: 0,
      top: '50%',
      transform: 'translateY(-50%)'
    },
    '& .MuiSlider-rail': {
      backgroundColor: theme.palette[props.color].main,
      opacity: 0.5,
      top: '50%',
      transform: 'translateY(-50%)'
    }
  }),

  root: {
    boxSizing: 'border-box',
    height: 40,
    padding: 0,
    '@media (pointer: coarse)': {
      padding: 0
    }
  },
  thumb: {
    height: 18,
    width: 18,
    marginLeft: -9,
    border: '4px solid currentColor',
    marginTop: -9,
    top: '50%'
  },
  active: { color: 'white' },
  track: {
    height: 5,
    borderRadius: 0,
    top: '50%',
    transform: 'translateY(-50%)'
  },
  rail: {
    height: 5,
    borderRadius: 0,
    opacity: 0.5,
    top: '50%',
    transform: 'translateY(-50%)'
  },
  mark: {
    display: 'none'
  },
  markLabel: {
    marginTop: '60px'
  }
}))(Slider);

const useStyles = makeStyles((theme) => ({
  mark: {
    color: theme.palette.fontcolor,
    fontVariantNumeric: 'tabular-nums',
    maxWidth: 'none',
    overflow: 'visible',
    textAlign: 'center',
    textOverflow: 'clip',
    whiteSpace: 'nowrap',
    width: 'max-content',
    '&[style*="left: 0%"], &[style*="left: 0%;"]': {
      textAlign: 'left',
      transform: 'translateX(0) !important'
    },
    '&[style*="left: 100%"], &[style*="left: 100%;"]': {
      textAlign: 'right',
      transform: 'translateX(-100%) !important'
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.8rem'
    }
  },
  sliderRoot: {
    minWidth: 0,
    width: '100%',
    touchAction: 'auto'
  },
  sliderTrackBox: {
    boxSizing: 'border-box',
    paddingLeft: SLIDER_THUMB_CLEARANCE,
    paddingRight: SLIDER_THUMB_CLEARANCE,
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      paddingLeft: 10,
      paddingRight: 10
    }
  },
  scaleRow: {
    color: theme.palette.fontcolor,
    display: 'flex',
    fontVariantNumeric: 'tabular-nums',
    gap: 8,
    marginTop: 0,
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.8rem'
    }
  },
  sliderLockRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: -14,
    paddingRight: 'clamp(6px, 2vw, 12px)',
    [theme.breakpoints.down('xs')]: {
      marginTop: 6,
      paddingRight: 10
    }
  }
}));

export function MinMaxSlider({ value, min, max, disabled, locked, slider_width, ...others }) {
  const marks = fitSliderMarks(GetMarks(min, undefined, max, '%'), slider_width);
  const classes = useStyles();
  const scaleMinWidth = getScaleMinWidth(marks);

  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.addEventListener(
        'touchstart',
        (e) => {
          const isThumb = e.target?.dataset.index;

          if (!isThumb) {
            e.stopPropagation();
          }
        },
        { capture: true }
      );
    }
  });

  return (
    <Box style={{ minWidth: scaleMinWidth ? `${scaleMinWidth}px` : 0, width: '100%' }}>
      <Box className={classes.sliderTrackBox}>
        {locked ? (
          <StyledSliderLocked
            getAriaValueText={valuetext}
            value={value}
            min={min}
            max={max}
            marks={sliderValueMarks(marks)}
            ref={ref}
            classes={{ root: classes.sliderRoot }}
            disabled={true}
            {...others}
          />
        ) : (
          <StyledSlider
            getAriaValueText={valuetext}
            value={value}
            min={min}
            max={max}
            marks={sliderValueMarks(marks)}
            ref={ref}
            classes={{ root: classes.sliderRoot }}
            disabled={disabled || false}
            {...others}
          />
        )}
      </Box>
      <SliderScale marks={marks} classes={classes} />
    </Box>
  );
}

function BaseSlider({
  name,
  min,
  mid,
  max,
  convert_to_min,
  convert_to_hhmmss,
  wrap_time_over_midnight,
  unit,
  disabled,
  locked,
  unit_key,
  mod_steps,
  slider_width,
  ...other
}) {
  let settings_borders_and_units = get_settings_borders_and_units(unit_key);
  if (settings_borders_and_units) {
    if (min === undefined) min = settings_borders_and_units[0];
    if (max === undefined) max = settings_borders_and_units[1];
    if (unit === undefined) unit = settings_borders_and_units[2];
  }

  let marks = GetMarks(min, mid, max, unit);

  // Convert marks from minutes to a string format "HH:MM"
  if (convert_to_min) {
    unit = '';

    marks.forEach((mark) => {
      // Convert value to hours and minutes
      let hours = Math.floor(mark.value / 60);
      const minutes = mark.value % 60;
      if (hours >= 24) {
        hours = hours % 24;
      }
      mark.label = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    });
  }

  if (convert_to_hhmmss) {
    marks.forEach((mark) => {
      let hours = 0;
      let minutes = 0;
      let seconds = 0;
      if (unit.indexOf('sec') !== -1) {
        hours = Math.floor(mark.value / 3600);
        minutes = Math.floor((mark.value % 3600) / 60);
        seconds = mark.value % 60;
      } else if (unit.indexOf('min') !== -1) {
        hours = Math.floor(mark.value / 60);
        minutes = mark.value % 60;
      }
      if (wrap_time_over_midnight && hours >= 24) {
        hours = hours % 24;
      }
      mark.label = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    });
    unit = '';
  }

  const classes = useStyles();
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.addEventListener(
        'touchstart',
        (e) => {
          const isThumb = e.target?.dataset.index;

          if (!isThumb) {
            e.stopPropagation();
          }
        },
        { capture: true }
      );
    }
  });

  marks = fitSliderMarks(marks, slider_width);
  const scaleMinWidth = getScaleMinWidth(marks);

  const stepSize = mod_steps ? (max - min) / mod_steps : 1;

  return (
    <Box style={{ minWidth: scaleMinWidth ? `${scaleMinWidth}px` : 0, width: '100%' }}>
      <Box className={classes.sliderTrackBox}>
        {locked ? (
          <StyledSliderLocked
            getAriaValueText={valuetext}
            marks={sliderValueMarks(marks)}
            step={stepSize}
            min={min}
            max={max}
            ref={ref}
            classes={{ root: classes.sliderRoot }}
            disabled={true}
            {...other}
          />
        ) : (
          <StyledSlider
            getAriaValueText={valuetext}
            marks={sliderValueMarks(marks)}
            step={stepSize}
            min={min}
            max={max}
            ref={ref}
            classes={{ root: classes.sliderRoot }}
            disabled={disabled || false}
            {...other}
          />
        )}
      </Box>
      <SliderScale marks={marks} classes={classes} />
    </Box>
  );
}

function FloatSlider({
  name,
  value,
  min,
  max,
  unit,
  disabled,
  locked,
  unit_key,
  num_digits,
  mod_digits,
  slider_width,
  ...other
}) {
  const adjusted_value = (Number.parseFloat(value) / 10 ** num_digits).toFixed(num_digits);

  let settings_borders_and_units = get_settings_borders_and_units(unit_key);
  if (settings_borders_and_units) {
    if (min === undefined) min = settings_borders_and_units[0];
    if (max === undefined) max = settings_borders_and_units[1];
    if (unit === undefined) unit = settings_borders_and_units[2];
  }

  const classes = useStyles();
  const marks = fitSliderMarks(GetMarks(min, undefined, max, unit, mod_digits), slider_width);
  const scaleMinWidth = getScaleMinWidth(marks);
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.addEventListener(
        'touchstart',
        (e) => {
          const isThumb = e.target?.dataset.index;

          if (!isThumb) {
            e.stopPropagation();
          }
        },
        { capture: true }
      );
    }
  });
  return (
    <Box style={{ minWidth: scaleMinWidth ? `${scaleMinWidth}px` : 0, width: '100%' }}>
      <Box className={classes.sliderTrackBox}>
        {locked ? (
          <StyledSliderLocked
            getAriaValueText={valuetext}
            marks={sliderValueMarks(marks)}
            step={0.1 / mod_digits}
            min={min}
            max={max}
            value={adjusted_value}
            ref={ref}
            classes={{ root: classes.sliderRoot }}
            disabled={true}
            {...other}
          />
        ) : (
          <StyledSlider
            getAriaValueText={valuetext}
            marks={sliderValueMarks(marks)}
            step={0.1 / mod_digits}
            min={min}
            max={max}
            value={adjusted_value}
            ref={ref}
            classes={{ root: classes.sliderRoot }}
            disabled={disabled || false}
            {...other}
          />
        )}
      </Box>
      <SliderScale marks={marks} classes={classes} />
    </Box>
  );
}

function NegativeSlider({
  name,
  value,
  min,
  max,
  unit,
  disabled,
  locked,
  unit_key,
  slider_width,
  ...other
}) {
  const adjusted_value = value - 127;

  let settings_borders_and_units = get_settings_borders_and_units(unit_key);
  if (settings_borders_and_units) {
    if (min === undefined) min = settings_borders_and_units[0];
    if (max === undefined) max = settings_borders_and_units[1];
    if (unit === undefined) unit = settings_borders_and_units[2];
  }

  const classes = useStyles();
  const marks = fitSliderMarks(GetMarks(min, undefined, max, unit), slider_width);
  const scaleMinWidth = getScaleMinWidth(marks);
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.addEventListener(
        'touchstart',
        (e) => {
          const isThumb = e.target?.dataset.index;

          if (!isThumb) {
            e.stopPropagation();
          }
        },
        { capture: true }
      );
    }
  });
  return (
    <Box style={{ minWidth: scaleMinWidth ? `${scaleMinWidth}px` : 0, width: '100%' }}>
      <Box className={classes.sliderTrackBox}>
        {locked ? (
          <StyledSliderLocked
            getAriaValueText={valuetext}
            marks={sliderValueMarks(marks)}
            min={min}
            max={max}
            value={adjusted_value}
            ref={ref}
            classes={{ root: classes.sliderRoot }}
            disabled={true}
            {...other}
          />
        ) : (
          <StyledSlider
            getAriaValueText={valuetext}
            marks={sliderValueMarks(marks)}
            min={min}
            max={max}
            value={adjusted_value}
            ref={ref}
            classes={{ root: classes.sliderRoot }}
            disabled={disabled || false}
            {...other}
          />
        )}
      </Box>
      <SliderScale marks={marks} classes={classes} />
    </Box>
  );
}

export function GraphSlider({ value, max, disabled, locked, ...others }) {
  const classes = useStyles();

  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.addEventListener(
        'touchstart',
        (e) => {
          const isThumb = e.target?.dataset.index;

          if (!isThumb) {
            e.stopPropagation();
          }
        },
        { capture: true }
      );
    }
  });
  return (
    <Fragment>
      {locked ? (
        <StyledSliderLocked
          getAriaValueText={valuetext}
          value={value}
          min={0}
          max={max}
          ref={ref}
          classes={{ markLabel: classes.mark, root: classes.sliderRoot }}
          disabled={true}
          {...others}
        />
      ) : (
        <StyledSlider
          getAriaValueText={valuetext}
          value={value}
          min={0}
          max={max}
          ref={ref}
          classes={{ markLabel: classes.mark, root: classes.sliderRoot }}
          disabled={disabled || false}
          {...others}
        />
      )}
    </Fragment>
  );
}
